/**
 * @file Throwing the equipped weapon away (issue #15).
 *
 * Pressing `T` hands the player's `Weapon` component to a new airborne entity
 * and leaves the player unarmed. Because the component itself moves, the throw
 * costs nothing to undo: walking back over the weapon restores the same stat
 * block.
 *
 * Runs between the player controller (which has just written this frame's aim)
 * and the enemy AI (which reacts to a player who is suddenly unarmed).
 */

import * as THREE from 'three';
import { System } from '../ecs/System.js';
import { Transform, PlayerControlled, Weapon } from '../components/index.js';
import { Action } from '../input/InputManager.js';
import { Events } from '../core/EventBus.js';
import { createThrownWeapon } from '../entities/WeaponDrop.js';

export class ThrowSystem extends System {
  static priority = 15;

  constructor() {
    super();
    /** @type {THREE.Vector3} @private */
    this._origin = new THREE.Vector3();
    /** @type {THREE.Vector3} @private */
    this._direction = new THREE.Vector3();
    /** @type {object} @private */
    this._thrownEvent = { weapon: null, source: null };
    /** @type {object} @private */
    this._changedEvent = { weapon: null };
    /** @type {object} @private */
    this._shakeEvent = { amount: 0 };
  }

  /**
   * @param {number} _dt Delta time in seconds.
   * @returns {void}
   */
  update(_dt) {
    if (!this.ctx.input.justPressed(Action.THROW)) return;

    for (const entity of this.world.query(PlayerControlled, Transform, Weapon)) {
      // A weapon the row marks unthrowable makes `T` a no-op — otherwise the
      // fallback knife could be thrown, and the fallback system would hand out a
      // second one the same frame (issue #122).
      if (!entity.get(Weapon).throwable) continue;
      this._throw(entity);
    }
  }

  /**
   * @param {import('../ecs/Entity.js').Entity} entity Thrower.
   * @returns {void}
   * @private
   */
  _throw(entity) {
    const { events, config } = this.ctx;
    const weapon = entity.get(Weapon);
    const transform = entity.get(Transform);

    this._aimDirection(entity, transform, this._direction);
    this._origin
      .copy(transform.position)
      .addScaledVector(this._direction, weapon.muzzleForward);
    this._origin.y = config.throwing.height;

    // Detach before the factory takes ownership: the weapon must belong to
    // exactly one entity at any instant.
    entity.remove(Weapon);

    createThrownWeapon(this.ctx, {
      position: this._origin,
      direction: this._direction,
      weapon,
      owner: entity
    });

    this._thrownEvent.weapon = weapon;
    this._thrownEvent.source = entity;
    events.emit(Events.WEAPON_THROWN, this._thrownEvent);
    // Null is the "unarmed" signal every weapon listener already has to handle.
    if (entity === this.ctx.player) this.ctx.playerWeaponKey = null;
    events.emit(Events.WEAPON_CHANGED, this._changedEvent);

    if (config.throwing.shake > 0) {
      this._shakeEvent.amount = config.throwing.shake;
      events.emit(Events.SHAKE, this._shakeEvent);
    }
  }

  /**
   * Throw direction: straight at the cursor when there is one, otherwise along
   * the body's facing, which is where a gamepad player is looking anyway.
   *
   * @param {import('../ecs/Entity.js').Entity} entity Thrower.
   * @param {import('../components/Transform.js').Transform} transform Its transform.
   * @param {THREE.Vector3} out Direction, normalised in place.
   * @returns {THREE.Vector3} The same `out` vector.
   * @private
   */
  _aimDirection(entity, transform, out) {
    const control = entity.get(PlayerControlled);
    const dx = control ? control.aimPoint.x - transform.position.x : 0;
    const dz = control ? control.aimPoint.z - transform.position.z : 0;

    if (dx * dx + dz * dz > 1e-6) return out.set(dx, 0, dz).normalize();
    return out.set(Math.sin(transform.rotationY), 0, Math.cos(transform.rotationY));
  }
}
